import GeneralUtils from '../../shared/utils/GeneralUtils';

/** Clears basket / checkout session keys after a successful place order. */
export function clearOrderBasketStorage() {
    const branchKey = localStorage.getItem('merchantBranchId');
    if (!GeneralUtils.isNullOrEmpty(branchKey)) {
        localStorage.removeItem(branchKey);
    }
    localStorage.removeItem('orderType');
    localStorage.removeItem('postCode');
    localStorage.removeItem('description');
    localStorage.removeItem('addressLine1');
    localStorage.removeItem('addressLine2');
    localStorage.removeItem('addressLine3');
    localStorage.removeItem('addressLine4');
    localStorage.removeItem('city');
    localStorage.removeItem('tableBssId');
}

const formatAddressLines = (address) => {
    if (!address || GeneralUtils.isNullOrEmpty(address)) {
        return null;
    }
    const parts = [
        address.addressLine1,
        address.addressLine2,
        address.addressLine3,
        address.addressLine4,
        address.city,
        address.postCode,
    ].filter((part) => !GeneralUtils.isNullOrEmpty(part));
    return parts.length > 0 ? parts.join(', ') : null;
};

const isTableOrderType = (orderType) =>
    orderType === 'EAT_IN' || orderType === 'Table Order';

const isDeliveryOrderType = (orderType) =>
    orderType === 'DELIVERY' || orderType === 'Delivery';

const isCollectionOrderType = (orderType) =>
    orderType === 'COLLECTION' || orderType === 'Collection';

/**
 * Enrich placeOrder API response with display fields when backend omits computed strings.
 * @param {object} response placeOrder BasketDTO
 * @param {object} checkoutState ApproveOrder / ConfirmOrder state snapshot
 */
export function enrichPlacedOrder(response, checkoutState) {
    if (!response) {
        return response;
    }
    const order = Object.assign({}, response);
    const orderType = order.orderType || (checkoutState && checkoutState.order && checkoutState.order.orderType);

    if (GeneralUtils.isNullOrEmpty(order.deliveryAddress)) {
        if (isTableOrderType(orderType)) {
            if (!GeneralUtils.isNullOrEmpty(order.eatInTableDisplayName)) {
                order.deliveryAddress = order.eatInTableDisplayName;
            } else if (order.tableNo != null) {
                order.deliveryAddress = `Table No: ${order.tableNo}`;
            } else if (checkoutState && checkoutState.tables && checkoutState.order) {
                const tableBssId = order.tableBssId || checkoutState.order.tableBssId;
                const table = checkoutState.tables.find((t) => String(t.bssId) === String(tableBssId));
                if (table) {
                    order.deliveryAddress = table.name || (table.tableNo != null ? `Table ${table.tableNo}` : 'Table');
                }
            }
        } else if (isDeliveryOrderType(orderType) && checkoutState && checkoutState.memberUserAddress) {
            order.deliveryAddress = formatAddressLines(checkoutState.memberUserAddress);
        } else if (isCollectionOrderType(orderType)) {
            order.deliveryAddress = 'Collection';
        }
    }

    if (GeneralUtils.isNullOrEmpty(order.totalPriceString) && order.totalPrice != null) {
        order.totalPriceString = `£${Number(order.totalPrice).toFixed(2)}`;
    }
    if (GeneralUtils.isNullOrEmpty(order.subTotalPriceString) && order.subTotalPrice != null) {
        order.subTotalPriceString = `£${Number(order.subTotalPrice).toFixed(2)}`;
    }

    if (GeneralUtils.isNullOrEmpty(order.orderStatusMember)) {
        order.orderStatusMember = order.orderStatus || 'ORDER_PLACED';
    }

    if (GeneralUtils.isNullOrEmpty(order.tradingName)) {
        const fromCheckout = checkoutState && checkoutState.order && checkoutState.order.tradingName;
        const fromStorage = GeneralUtils.getMemberStoreTradingName();
        order.tradingName = fromCheckout || fromStorage || null;
    }

    if (GeneralUtils.isNullOrEmpty(order.paymentTypeDisplay)) {
        order.paymentTypeDisplay = resolvePaymentTypeDisplay(order, checkoutState);
    }

    return order;
}

export const formatPaymentTypeLabel = (value) => {
    if (GeneralUtils.isNullOrEmpty(value)) {
        return null;
    }
    const normalized = String(value).trim();
    const map = {
        CARD: 'Card',
        WALLET: 'Card',
        'APPLE PAY': 'Apple Pay',
        'PAY PAL': 'PayPal',
        'PAY AT COUNTER': 'Pay at counter',
    };
    if (map[normalized]) {
        return map[normalized];
    }
    return normalized
        .replace(/\s*\((DELIVERY|COLLECTION)\)\s*$/i, '')
        .replace(/\s+/g, ' ')
        .trim();
};

/** Human-readable payment method for confirmation / My Orders. */
export function resolvePaymentTypeDisplay(order, checkoutState) {
    if (!order) {
        return null;
    }
    if (!GeneralUtils.isNullOrEmpty(order.paymentLabel)) {
        return String(order.paymentLabel).trim();
    }

    const paymentType = order.paymentType;
    const paymentOption = checkoutState && checkoutState.paymentOption;

    if (!GeneralUtils.isNullOrEmpty(paymentType)) {
        if (paymentType === 'CARD' && Number.isInteger(paymentOption) && checkoutState && Array.isArray(checkoutState.cardList)) {
            const card = checkoutState.cardList.find((c) => c.id === paymentOption);
            if (card && !GeneralUtils.isNullOrEmpty(card.maskedPan)) {
                return `Card ${card.maskedPan}`;
            }
            if (card && !GeneralUtils.isNullOrEmpty(card.panStar)) {
                return `Card ${card.panStar}`;
            }
        }
        return formatPaymentTypeLabel(paymentType);
    }

    if (paymentOption == null) {
        return null;
    }

    if (Number.isInteger(paymentOption) && checkoutState && Array.isArray(checkoutState.cardList)) {
        const card = checkoutState.cardList.find((c) => c.id === paymentOption);
        if (card && !GeneralUtils.isNullOrEmpty(card.maskedPan)) {
            return `Card ${card.maskedPan}`;
        }
        return 'Card';
    }

    const settings = (checkoutState && checkoutState.paymentSettings) || [];
    const match = settings.find((s) => s.id === paymentOption || s.name === paymentOption);
    if (match && !GeneralUtils.isNullOrEmpty(match.name)) {
        return formatPaymentTypeLabel(match.name);
    }

    return formatPaymentTypeLabel(paymentOption);
}

export function getOrderLocationLabel(orderType) {
    if (isTableOrderType(orderType)) {
        return 'Table';
    }
    if (isDeliveryOrderType(orderType)) {
        return 'Delivery To';
    }
    if (isCollectionOrderType(orderType)) {
        return 'Collection';
    }
    return 'Location';
}

export function shouldShowOrderLocation(order) {
    if (!order) {
        return false;
    }
    const orderType = order.orderType;
    if (isCollectionOrderType(orderType)) {
        return false;
    }
    if (isTableOrderType(orderType) || isDeliveryOrderType(orderType)) {
        return !GeneralUtils.isNullOrEmpty(order.deliveryAddress);
    }
    return !GeneralUtils.isNullOrEmpty(order.deliveryAddress);
}

/** 3DS / payment redirect landed with success (Opayo, Elavon, DNA, Apple Pay return via webhook). */
export function isOrderPlacedReturnUrl() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('orderPlacedReturn') === '1') {
        return true;
    }
    if (params.get('dnaReturn') === '1') {
        return true;
    }
    const successfulMessage = params.get('successfulMessage');
    return successfulMessage === 'true';
}

export function isOrderPlacedReturnFailure() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('dnaFail') === '1') {
        return true;
    }
    const successfulMessage = params.get('successfulMessage');
    return successfulMessage === 'false';
}

export function clearOrderPlacedReturnParams() {
    GeneralUtils.removeQueryParam('orderPlacedReturn');
    GeneralUtils.removeQueryParam('dnaReturn');
    GeneralUtils.removeQueryParam('dnaFail');
    GeneralUtils.removeQueryParam('successfulMessage');
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Load placed order after 3DS / external payment return (latest previous order or basketId param).
 * @param {import('../../shared/service/BasketService').default} basketService
 */
export async function loadPlacedOrderForConfirmation(basketService) {
    const basketId = GeneralUtils.getParameterByName('basketId');
    const attempts = basketId ? 1 : 4;

    for (let i = 0; i < attempts; i++) {
        if (!GeneralUtils.isNullOrEmpty(basketId)) {
            const byId = await basketService.findBasketById(basketId);
            if (!GeneralUtils.isNullOrEmpty(byId) && GeneralUtils.isNullOrEmpty(byId.type)) {
                return byId;
            }
        }

        const grid = await basketService.findLatestPreviousOrder();
        if (!GeneralUtils.isNullOrEmpty(grid) && GeneralUtils.isNullOrEmpty(grid.type)) {
            const rows = grid.data || grid.rows || grid.content;
            if (Array.isArray(rows) && rows.length > 0) {
                return rows[0];
            }
        }

        if (i < attempts - 1) {
            await sleep(700);
        }
    }

    return null;
}
